import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as THREE from 'three';
import { OrbitControls } from './OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import glb from '../../../models/Alexander_noHole_OBJ.glb';
import nx from '../../../assets/textures/nx.jpg';
import ny from '../../../assets/textures/ny.jpg';
import nz from '../../../assets/textures/nz.jpg';
import px from '../../../assets/textures/px.jpg';
import py from '../../../assets/textures/py.jpg';
import pz from '../../../assets/textures/pz.jpg';
import styles from './threeModel.css';

async function prepareThreeScene(width, height, parent) {
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setClearColor('#FFFFFF');
  renderer.setSize(width, height);
  // renderer.outputEncoding = THREE.sRGBEncoding;

  const scene = new THREE.Scene();
  const pivot = new THREE.Group();

  const camera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 1, 5000);
  const FOV = window.innerWidth >= 600 ? 50 : 80;
  camera.fov = FOV;
  camera.position.z = 130;
  camera.aspect = width / height;
  camera.updateProjectionMatrix();

  const controls = new OrbitControls(camera, parent.current);
  controls.enableZoom = false;
  controls.enablePan = false;
  controls.enableDamping = true;
  controls.dampingFactor = 0.05;
  controls.disableTouch = true;

  window.addEventListener('mousedown', e => {
    const el = e.target;
    if (el instanceof HTMLInputElement) {
      controls.enabled = false;
      el.focus();
    } else {
      controls.enabled = true;
    }
  });

  const light = new THREE.DirectionalLight('white', 1);
  light.position.set(-1, 2, 4);

  const urls = [nx, ny, nz, px, py, pz];
  const reflectionCube = new THREE.CubeTextureLoader().load(urls);
  reflectionCube.minFilter = THREE.LinearFilter;
  const material = new THREE.MeshStandardMaterial({
    emissive: new THREE.Color(0x666666),
    metalness: 0.5,
    roughness: 0.1,
    envMapIntensity: 0.8,
    envMap: reflectionCube,
    transparent: true,
    opacity: 1,
  });

  const head = (await new Promise(resolve => {
    const loader = new GLTFLoader();
    loader.load(glb, object => resolve(object));
  })).scene;

  head.traverse(child => {
    if (child instanceof THREE.Mesh) {
      child.material = material;
      child.material.flatShading = true;
      child.material.needsUpdate = true;
    }
  });

  const box = new THREE.Box3().setFromObject(head);
  box.getCenter(head.position);
  head.position.multiplyScalar(-1);

  scene.add(pivot);
  pivot.add(head);
  camera.add(light);
  scene.add(camera);

  const renderScene = () => {
    renderer.render(scene, camera);
    controls.update();
  };

  let frameId = null;
  // let time = 0;

  const animate = () => {
    // time = new Date().getTime();
    // pivot.position.y = Math.cos(time * 0.0015) * 4;
    pivot.rotation.y += 0.009;
    pivot.rotation.x += 0.005;

    renderScene();
    frameId = window.requestAnimationFrame(animate);
  };

  return {
    el: renderer.domElement,
    start: () => {
      if (!frameId) {
        frameId = requestAnimationFrame(animate);
      }
    },
    stop: () => {
      cancelAnimationFrame(frameId);
      frameId = null;
    },
    handleResize: (w, h) => {
      renderer.setSize(w, h);
      camera.aspect = w / h;
      const FOV = window.innerWidth >= 600 ? 50 : 80;
      camera.fov = FOV;
      camera.updateProjectionMatrix();
      renderScene();
    },
    dispose: () => {
      scene.remove(head);
      material.dispose();
    },
  };

}

const ThreeModel = ({ parent }) => {
  const mount = useRef(null);
  const threeSceneRef = useRef();

  useEffect(() => {
    const width = mount.current.clientWidth;
    const height = mount.current.clientHeight;

    const resizeHandler = () => {
      if (threeSceneRef.current && mount.current) {
        threeSceneRef.current.handleResize(mount.current.clientWidth, mount.current.clientHeight);
      }
    };

    prepareThreeScene(width, height, parent).then(threeScene => {
      threeSceneRef.current = threeScene;
      threeSceneRef.current.start();

      if (mount.current) {
        mount.current.appendChild(threeSceneRef.current.el);
      }

      window.addEventListener('resize', resizeHandler);
      resizeHandler();
    });

    return () => {
      if (threeSceneRef.current) {
        threeSceneRef.current.stop();
        mount.current.removeChild(threeSceneRef.current.el);
        threeSceneRef.current.dispose();
      }

      window.removeEventListener('resize', resizeHandler);
    };
  }, []);

  return (
    <div className={styles.wrapper}>
      <div className={styles.three} ref={mount} />
    </div>
  );
};

ThreeModel.propTypes = {
  parent: PropTypes.any,
};

export default ThreeModel;
